import ResultsEmptyStateIcon from '@assets/icons/table/results-empty-state.svg';
import { BaseInput, BaseSwitch } from '@components';
import { msg } from '@lingui/core/macro';
import { CommonMultiSearchSelect } from '@routes/sidebar/admin-center/___shared___/components/multi-search-select';
import {
    EDITABLE_FIELDS,
    NOT_SET_LABEL,
} from '@routes/sidebar/admin-center/___shared___/components/tables/constants.ts';
import {
    FilterEmptyTextWrapper,
    NoFilterText,
    StyledDatePicker,
    StyledSelect,
} from '@routes/sidebar/admin-center/___shared___/components/tables/regular/styled.ts';
import type { SelectOption } from '@routes/sidebar/admin-center/___shared___/components/tables/types.ts';
import { ToggleSwitchWrapper } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___pages___/references/ui/toggle-switch/styled.tsx';
import { MatricesRanksSelect } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/ranks';
import { MatricesVesselsSelect } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-category-selector/vessels';
import { CustomSelectDropdownMatrices } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-select-dropdown';
import {
    Label,
    SubLabel,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/___shared___/components/custom-select-dropdown/styled.tsx';
import {
    DEPARTMENT,
    LEVEL,
    VESSEL_GROUP,
    VESSEL_TYPE,
} from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/constants.ts';
import type { MatricesCustomSelectData } from '@routes/sidebar/admin-center/tenant-client/___pages___/matrices/ui/table/utils.ts';
import { translateItem } from '@utils/translations.ts';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import type { Rule } from 'antd/es/form';
import dayjs from 'dayjs';

export const defaultRowClassName = <T extends { __allInactive?: boolean; isActive?: boolean }>(row: T): string => {
    if (row.__allInactive) return 'row-inactive';

    const active = (row as any).isActive ?? true;
    return active ? 'row-active' : 'row-inactive';
};

export const toCamelKey = (value: string) =>
    value
        .replace(/[^a-zA-Z0-9]+/g, ' ')
        .trim()
        .split(' ')
        .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1)))
        .join('')
        .replace(/[^a-zA-Z0-9]/g, '');

export const showNoSetValue = (value: any) => {
    if (Array.isArray(value)) {
        return value.length ? value.join(', ') : NOT_SET_LABEL();
    }

    const isEmpty = value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

    if (isEmpty) {
        return '-';
    }

    return String(value);
};

export const FilterEmptyText = ({ title }: { title: string }) => (
    <FilterEmptyTextWrapper>
        <ResultsEmptyStateIcon />
        <NoFilterText>{title}</NoFilterText>
    </FilterEmptyTextWrapper>
);

interface EditorSwitchProps {
    col: any & {
        namePath?: (string | number)[];
        initialValue?: unknown;
        disabled?: boolean;
    };
    opts?: {
        getOptions?: (dataSource: string, record?: Record<string, any>) => SelectOption[];
        placeholders?: { select?: string; dataPicker?: string; input?: string };
        dateFormate?: string;
        customRankAndVesselSelectData?: {
            ranksData?: MatricesCustomSelectData;
            vesselsData?: MatricesCustomSelectData;
        };
    };
    allowSingleSearch?: boolean;
    allowMultiSearch?: boolean;
    onSingleChange?: (value: string) => void;
    onSingleSearch?: (value: string) => void;
    onMultiChange?: (value: string[]) => void;
    onMultiSearch?: (value: string) => void;
    onToggleChange?: (checked: boolean) => void;
    form?: FormInstance<any>;
    record?: Record<string, any>;
}

/**
 * to see how was implemented  loading={col.loading}  for  SINGLE_SELECT/MULTI_SELECT search in project
 * loading: isGetVesselEqMakersPending
 */

export const editorSwitch = ({
    col,
    opts,
    onSingleChange,
    onSingleSearch,
    onMultiChange,
    onMultiSearch,
    allowSingleSearch = true,
    allowMultiSearch = true,
    onToggleChange,
    form,
    record,
}: EditorSwitchProps) => {
    if (!col.editable) return null;

    const STANDARD_REQUIRED_MESSAGE = translateItem(msg`Field is required`);

    const rules: Rule[] = [
        ...(col.require ? [{ required: true, message: STANDARD_REQUIRED_MESSAGE }] : []),
        ...(col.rules && col.rules.length ? col.rules : []),
    ];

    const MULTI_RULES: Rule[] = col.require
        ? [
              {
                  required: true,
                  type: 'array',
                  min: 1,
                  message: STANDARD_REQUIRED_MESSAGE,
              },
          ]
        : [];

    const placeholder = opts?.placeholders?.select ?? translateItem(msg`Select`);
    const dataPickerPlaceholder = opts?.placeholders?.dataPicker ?? translateItem(msg`Select date`);
    const inputPlaceholder = opts?.placeholders?.input ?? translateItem(msg`Enter`);

    const STANDARD_LABEL_NAME = 'label';

    // for vessels and ranks custom selects
    const searchVesselPlaceholder = translateItem(msg`Search subtypes...`);
    const searchRanksPlaceholder = translateItem(msg`Search ranks...`);

    const selectOptions = col.dataSource && opts?.getOptions ? opts.getOptions(col.dataSource, record) : [];

    const {
        INPUT_TEXT,
        INPUT_NUMBER,
        SINGLE_SELECT,
        MULTI_SELECT,
        TOGGLE,
        TOGGLE_CONTROLLED,
        DATE_PICKER,
        CUSTOM_SINGLE_SELECT_MATRICES,
        CUSTOM_SINGLE_SELECT,
        CUSTOM_MULTI_SELECT_MATRICES_RANKS,
        CUSTOM_MULTI_SELECT_MATRICES_VESSELS,
        CUSTOM_COMMON_MULTI_SELECT,
    } = EDITABLE_FIELDS;

    const resetStateOnClose = (open: boolean) => {
        if (!open) {
            if (col.type === SINGLE_SELECT && onSingleSearch) {
                onSingleSearch('');
            }
            if (col.type === MULTI_SELECT && onMultiSearch) {
                onMultiSearch('');
            }
        }
    };

    switch (col.type) {
        case INPUT_TEXT:
            return (
                <Form.Item name={col.dataIndex} rules={rules} style={{ margin: 0 }}>
                    <BaseInput placeholder={inputPlaceholder} />
                </Form.Item>
            );
        case INPUT_NUMBER:
            return (
                <Form.Item name={col.dataIndex} rules={rules} noStyle>
                    <BaseInput type="number" />
                </Form.Item>
            );
        case SINGLE_SELECT:
            return (
                <Form.Item name={col.dataIndex} rules={rules} style={{ margin: 0 }}>
                    <StyledSelect
                        allowClear={!col.require}
                        loading={col.loading}
                        disabled={col.disabled}
                        placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                        options={selectOptions}
                        showSearch={
                            allowSingleSearch
                                ? {
                                      optionFilterProp: STANDARD_LABEL_NAME,
                                      onSearch: onSingleSearch,
                                  }
                                : false
                        }
                        onChange={onSingleChange}
                        onOpenChange={resetStateOnClose}
                    />
                </Form.Item>
            );
        // VIEW MODE → for custom single select (assessors)
        case CUSTOM_SINGLE_SELECT:
            return (
                <StyledSelect
                    value={col.value}
                    allowClear={false}
                    loading={col.loading}
                    disabled={col.disabled}
                    placeholder={col.placeholder ?? (col.require ? placeholder : NOT_SET_LABEL())}
                    options={selectOptions}
                    showSearch={
                        allowSingleSearch
                            ? {
                                  optionFilterProp: STANDARD_LABEL_NAME,
                                  onSearch: onSingleSearch,
                              }
                            : false
                    }
                    onChange={onSingleChange}
                    onOpenChange={resetStateOnClose}
                />
            );
        case MULTI_SELECT: {
            return (
                <Form.Item name={col.dataIndex} rules={MULTI_RULES} style={{ margin: 0 }}>
                    <StyledSelect
                        allowClear={!col.require}
                        mode="multiple"
                        loading={col.loading}
                        disabled={col.disabled}
                        placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                        options={selectOptions}
                        showSearch={
                            allowMultiSearch
                                ? {
                                      onSearch: onMultiSearch,
                                  }
                                : false
                        }
                        onChange={onMultiChange}
                        onOpenChange={resetStateOnClose}
                        {...(col.enableResponsiveCollapse ? { maxTagCount: 'responsive' } : {})}
                    />
                </Form.Item>
            );
        }
        case CUSTOM_SINGLE_SELECT_MATRICES: {
            // standard options meaning just label and value
            const isStandardOptions = selectOptions.every((o) => {
                const keys = Object.keys(o);
                return keys.length === 2 && keys.includes('value') && keys.includes(STANDARD_LABEL_NAME);
            });

            // use for optionLabelProp and optionFilterProp
            const optionLabelProp = isStandardOptions ? STANDARD_LABEL_NAME : 'displayLabel';
            const optionFilterProp = optionLabelProp;

            const options = isStandardOptions
                ? selectOptions
                : selectOptions.map(({ value, label, displayLabel, subLabel }) => ({
                      value,
                      displayLabel,
                      label: (
                          <div>
                              <Label>{label}</Label>
                              {!!subLabel && <SubLabel>{subLabel}</SubLabel>}
                          </div>
                      ),
                  }));

            return (
                <Form.Item name={col.dataIndex} rules={rules} style={{ margin: 0 }}>
                    <StyledSelect
                        popupRender={(menu) => <CustomSelectDropdownMatrices menu={menu} />}
                        optionLabelProp={optionLabelProp}
                        allowClear={!col.require}
                        loading={col.loading}
                        disabled={col.disabled}
                        placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                        options={options}
                        showSearch={
                            allowMultiSearch
                                ? {
                                      optionFilterProp,
                                      onSearch: onMultiSearch,
                                  }
                                : false
                        }
                        onChange={onMultiChange}
                        onOpenChange={resetStateOnClose}
                    />
                </Form.Item>
            );
        }
        case CUSTOM_COMMON_MULTI_SELECT: {
            const { showSearch, onSearch, onLoadOptions, hasMore } = col.customMultiSelectProps ?? {};

            return (
                <CommonMultiSearchSelect
                    name={col.dataIndex}
                    rules={MULTI_RULES}
                    placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                    allowClear={!col.require}
                    loading={col.loading}
                    disabled={col.disabled}
                    showSearch={showSearch ?? allowMultiSearch}
                    onChange={onMultiChange}
                    onSearch={onSearch ?? onMultiSearch}
                    onLoadOptions={onLoadOptions}
                    hasMore={hasMore}
                    options={selectOptions}
                    searchPlaceholder={inputPlaceholder}
                />
            );
        }
        case CUSTOM_MULTI_SELECT_MATRICES_VESSELS: {
            const {
                levelOneItems = [],
                levelTwoItems = [],
                subItems = [],
            } = opts?.customRankAndVesselSelectData?.vesselsData ?? {};
            return (
                <MatricesVesselsSelect
                    name={col.dataIndex}
                    rules={MULTI_RULES}
                    placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                    allowClear={!col.require}
                    loading={col.loading}
                    deselectOnly={col.deselectOnly}
                    disabled={col.disabled}
                    showSearch={allowMultiSearch}
                    onChange={onMultiChange}
                    onSearch={onMultiSearch}
                    levelOneLabel={VESSEL_GROUP}
                    levelTwoLabel={VESSEL_TYPE}
                    levelOneItems={levelOneItems}
                    levelTwoItems={levelTwoItems}
                    subItems={subItems}
                    searchText={searchVesselPlaceholder}
                />
            );
        }
        case CUSTOM_MULTI_SELECT_MATRICES_RANKS: {
            const {
                levelOneItems = [],
                levelTwoItems = [],
                subItems = [],
            } = opts?.customRankAndVesselSelectData?.ranksData ?? {};

            return (
                <MatricesRanksSelect
                    name={col.dataIndex}
                    rules={MULTI_RULES}
                    placeholder={col.require ? placeholder : NOT_SET_LABEL()}
                    allowClear={!col.require}
                    loading={col.loading}
                    deselectOnly={col.deselectOnly}
                    disabled={col.disabled}
                    showSearch={allowMultiSearch}
                    onChange={onMultiChange}
                    onSearch={onMultiSearch}
                    levelOneLabel={DEPARTMENT}
                    levelTwoLabel={LEVEL}
                    levelOneItems={levelOneItems}
                    levelTwoItems={levelTwoItems}
                    subItems={subItems}
                    searchText={searchRanksPlaceholder}
                />
            );
        }
        case TOGGLE:
            return (
                <ToggleSwitchWrapper $isFixed={true}>
                    <Form.Item
                        name={col.namePath ?? col.dataIndex}
                        initialValue={col.initialValue}
                        valuePropName="checked"
                        noStyle
                    >
                        <BaseSwitch
                            disabled={col.disabled}
                            onChange={(checked) => {
                                const v = !!checked;

                                if (onToggleChange) onToggleChange(v);
                                else col.onToggleChange?.(v);
                            }}
                        />
                    </Form.Item>
                </ToggleSwitchWrapper>
            );
        case TOGGLE_CONTROLLED:
            return (
                <ToggleSwitchWrapper $isFixed={true}>
                    <Form.Item
                        name={col.namePath ?? col.dataIndex}
                        initialValue={col.initialValue}
                        valuePropName="checked"
                        noStyle
                        getValueFromEvent={(checked) => {
                            const value = Boolean(checked);
                            col.onToggleChange?.(value, record);

                            return form?.getFieldValue(col.namePath ?? col.dataIndex);
                        }}
                    >
                        <BaseSwitch disabled={col.disabled} />
                    </Form.Item>
                </ToggleSwitchWrapper>
            );
        case DATE_PICKER:
            return (
                <Form.Item
                    name={col.dataIndex}
                    rules={rules}
                    style={{ margin: 0 }}
                    getValueProps={(value: unknown) => {
                        if (!value) return { value: null };
                        const d = dayjs(value as any);
                        return { value: d.isValid() ? d : null };
                    }}
                >
                    <StyledDatePicker
                        disabled={col.disabled}
                        placeholder={dataPickerPlaceholder}
                        format={opts?.dateFormate ?? ''}
                    />
                </Form.Item>
            );
        default:
            return null;
    }
};
